import { useCallback, useMemo, useRef, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import { NotificationEnum } from "@graphql/generated/graphql.g";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  NotificationFilterForm,
  NotificationFilterFormRef,
  NotificationFilterFormValues,
} from "@components/Forms";
import {
  NotificationsTable,
  NotificationsViewMode,
} from "@components/Tables";
import {
  NotificationsHistoryTableConfig,
  NotificationsTableConfig,
  areFiltersActive,
  getDefaultFilterValues,
} from "@components/Tables/NotificationsTable/NotificationsTableConfig";
import { NotificationsTableColumnCustomization } from "@components/Tables/NotificationsTable/UI/NotificationsTableColumnCustomization";
import {
  DEFAULT_ACTIVE_NOTIFICATION_TABLE_COLUMN_SETTINGS,
  DEFAULT_HISTORY_NOTIFICATION_TABLE_COLUMN_SETTINGS,
  DEFAULT_NOTIFICATION_TABLE_COLUMN_ORDER,
  NotificationTableColumns,
} from "@components/Tables/NotificationsTable/UI/NotificationsTableColumns";

import { Box } from "@design-system/Box/Box";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";
import { NotificationDetailsPanel } from "@components/Panels/NotificationDetailsPanel";

import { routes } from "@pages/routes";

import {
  sortingFromSearchParams,
  sortingToSearchParams,
} from "@utils/table/tableSearchParams";

type Props = {
  viewMode?: NotificationsViewMode;
  initialPageIndex?: number;
};

const notificationsRoute = routes.portal.notifications;

const EMPTY_SORTING: SortingState = [];
const HISTORY_DEFAULT_SORTING: SortingState = [
  { id: NotificationTableColumns.resolvedAt, desc: true },
];

const toFilterValues = (
  searchParams: ReturnType<
    typeof notificationsRoute.$deserializeSearchParams
  >,
): NotificationFilterFormValues => ({
  clientId: searchParams.clientId,
  notificationType: searchParams.notificationType
    ? (searchParams.notificationType as NotificationEnum)
    : undefined,
  applicationNameSearch: searchParams.applicationNameSearch,
  resolvedById: searchParams.resolvedById,
  resolvedAt_Gte: searchParams.resolvedAt_Gte,
  resolvedAt_Lte: searchParams.resolvedAt_Lte,
});

export const NotificationsCard = ({
  viewMode = "active",
  initialPageIndex = 0,
}: Props) => {
  const isHistoryView = viewMode === "history";
  const tableConfig = isHistoryView
    ? NotificationsHistoryTableConfig
    : NotificationsTableConfig;
  const defaultColumnSettings = isHistoryView
    ? DEFAULT_HISTORY_NOTIFICATION_TABLE_COLUMN_SETTINGS
    : DEFAULT_ACTIVE_NOTIFICATION_TABLE_COLUMN_SETTINGS;
  const defaultSorting = isHistoryView
    ? HISTORY_DEFAULT_SORTING
    : EMPTY_SORTING;

  const [searchParams, setSearchParams] =
    useTypedSearchParams(notificationsRoute);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const filterValues = useMemo(
    () => toFilterValues(searchParams),
    [searchParams],
  );
  const sorting = useMemo(
    () =>
      sortingFromSearchParams(
        searchParams.sort,
        searchParams.dir,
        defaultSorting,
      ),
    [searchParams.sort, searchParams.dir, defaultSorting],
  );

  const filterFormRef = useRef<NotificationFilterFormRef>(null);

  const syncToUrl = useCallback(
    (values: NotificationFilterFormValues, nextSorting: SortingState) => {
      const { sort, dir } = sortingToSearchParams(nextSorting);
      setSearchParams(
        {
          clientId: values.clientId,
          notificationType: values.notificationType,
          applicationNameSearch: values.applicationNameSearch,
          resolvedById: isHistoryView ? values.resolvedById : undefined,
          resolvedAt_Gte: isHistoryView ? values.resolvedAt_Gte : undefined,
          resolvedAt_Lte: isHistoryView ? values.resolvedAt_Lte : undefined,
          sort,
          dir,
        },
        { untypedSearchParams: true },
      );
    },
    [setSearchParams, isHistoryView],
  );

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues, isHistoryView);
  }, [filterValues, isHistoryView]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    syncToUrl(getDefaultFilterValues(), sorting);
  }, [syncToUrl, sorting]);

  const handleFilterChange = useCallback(
    (newValues: NotificationFilterFormValues) => {
      syncToUrl(newValues, sorting);
      setIsFilterPanelOpen(false);
    },
    [syncToUrl, sorting],
  );

  const handleApplyFilters = useCallback(() => {
    if (filterFormRef.current) {
      syncToUrl(filterFormRef.current.getCurrentValues(), sorting);
      setIsFilterPanelOpen(false);
    }
  }, [syncToUrl, sorting]);

  const handleSortingChange = useCallback(
    (nextSorting: SortingState) => {
      syncToUrl(filterValues, nextSorting);
    },
    [syncToUrl, filterValues],
  );

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnSettingsOpen(false);
  }, []);

  return (
    <TableColumnSettingsProvider
      tableId={tableConfig.tableId}
      defaultColumnSettings={defaultColumnSettings}
      defaultColumnOrder={DEFAULT_NOTIFICATION_TABLE_COLUMN_ORDER}
    >
      <PortalHeaderActions>
        <Box direction="row" gap={8} align="center">
          {filtersActive && (
            <Text size={12} color={"textSecondary"}>
              Filters are applied
            </Text>
          )}
          <IconButton
            size="sm"
            icon="faFilter"
            onClick={handleFilterClick}
            outline={"sm"}
            showBubble={filtersActive}
            tooltip="Show filters"
          />
          <IconButton
            size="sm"
            icon="faTableColumns"
            onClick={handleColumnSettingsClick}
            outline={"sm"}
            tooltip="Show/Hide columns"
          />
        </Box>
      </PortalHeaderActions>
      <NotificationsTable
        initialPageIndex={initialPageIndex}
        viewMode={viewMode}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        {...(filterValues.clientId && {
          clientId: filterValues.clientId,
        })}
        {...(filterValues.notificationType && {
          notificationType: filterValues.notificationType,
        })}
        {...(filterValues.applicationNameSearch && {
          applicationNameSearch: filterValues.applicationNameSearch,
        })}
        {...(isHistoryView &&
          filterValues.resolvedById && {
            resolvedById: filterValues.resolvedById,
          })}
        {...(isHistoryView &&
          filterValues.resolvedAt_Gte && {
            resolvedAt_Gte: filterValues.resolvedAt_Gte,
          })}
        {...(isHistoryView &&
          filterValues.resolvedAt_Lte && {
            resolvedAt_Lte: filterValues.resolvedAt_Lte,
          })}
      />

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title={
          isHistoryView ? "Notification History Filters" : "Notification Filters"
        }
      >
        <NotificationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
          showHistoryFilters={isHistoryView}
        />
      </SlideInPanel>

      <NotificationsTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
        defaultColumnSettings={defaultColumnSettings}
      />
      <NotificationDetailsPanel />
    </TableColumnSettingsProvider>
  );
};
